/** Role-aware access — the finalized ACCESS_MATRIX.md ("finalized with Tom"),
 * encoded as page visibility per role.
 *
 * SAFETY RULE (do not weaken): admin / manager / member keep FULL access to
 * everything except the admin-only surfaces below. Tom is admin; the rest of
 * the team today is manager/member — so nobody currently loses a page. Only
 * the *named restricted roles* (acquisitions / tc / dispo / bookkeeper / va),
 * which aren't provisioned yet, are gated — this is future-ready and matches
 * the matrix the day those logins are created. An UNKNOWN role also defaults
 * to full, so a typo in `profiles.role` can never silently lock someone out.
 *
 * This is UI polish on top of the real enforcement: restricted roles are
 * already blocked at the database / server slice (backend _RESTRICTED), so
 * hiding a nav item is convenience, not the security boundary.
 *
 * NOTE: per-SECTION permissions ARE in v2 — Settings → Users & Roles writes the
 * PERMS blob (None/View/Edit per section), which the write door accepts and
 * pageEditable() below reads. What is still not ported from v1's auth-users.js
 * is the finer-grained per-user overrides: hiddenFields and leadScope. Those
 * have no v2 equivalent, so `profiles` role + PERMS is the whole picture. */

/** The app's nav pages, in sidebar order (drives firstAllowedPage fallback). */
export const NAV_PAGES = [
  "dashboard", "properties", "tasks", "inbox", "mailbox", "messages", "dialer", "notifications", "hot", "deals",
  "wholesale", "investors", "buyers", "dealinbox", "roi", "contacts", "calendar", "team", "analytics", "intel",
  "pricedrops", "agent", "facebook", "materials", "expenses", "companyexp", "utilities", "scrapers", "bizfiles", "settings",
] as const;

export type Page = (typeof NAV_PAGES)[number];

/** Human labels for each page (mirrors the sidebar text) — used by the Settings
 * per-user permission editor so an admin sees "Deal Inbox", not "dealinbox". */
export const PAGE_LABELS: Record<Page, string> = {
  dashboard: "Dashboard", properties: "Properties", tasks: "Tasks", inbox: "Inbox",
  mailbox: "Mailbox", messages: "Messages", dialer: "Dialer", notifications: "Notifications", hot: "Hot", deals: "Transactions",
  wholesale: "Wholesale", investors: "Investors", buyers: "Cash Buyers", dealinbox: "Deal Inbox", roi: "ROI",
  contacts: "Contacts", calendar: "Calendar", team: "Team", analytics: "Analytics",
  intel: "Market Intel", pricedrops: "Price Reductions", agent: "Text Agent",
  facebook: "Facebook",
  materials: "Materials", expenses: "Property Expenses", companyexp: "Company Expenses",
  utilities: "Utilities", scrapers: "Scrapers", bizfiles: "Business Files", settings: "Settings",
};

/** Roles that see everything (minus admin-only surfaces). */
const FULL = new Set(["admin", "manager", "member"]);

/** Pages only an admin may see (matrix: Business Files = Admin only — Manager
 * and every other role are "—"). Settings is NOT admin-only: it's a full-roles
 * page (admin/manager/member) so managers keep their v1 "full access" — the
 * genuinely admin-only parts inside it (user management, integrations connect,
 * destructive tools) are read-only / stay in v1 for everyone in Phase A, so the
 * page itself is safe to show. The 5 restricted roles don't get it (it's absent
 * from their matrix sets below). */
const ADMIN_ONLY = new Set<Page>(["bizfiles"]);

/** Named restricted roles → the exact pages the matrix grants them. Absence
 * from this map (unknown role) means "treat as full" (never lock out). */
const RESTRICTED: Record<string, Set<Page>> = {
  // Assigned leads + own messages/emails; no deals/financials/wholesale/admin.
  acquisitions: new Set<Page>(["dashboard", "properties", "hot", "intel", "contacts", "inbox", "mailbox", "dialer", "tasks", "calendar"]),
  // Assigned transactions + their contacts/emails; no lead pipeline/financials.
  tc: new Set<Page>(["dashboard", "deals", "contacts", "mailbox", "tasks", "calendar"]),
  // Wholesale + buyers + own messages.
  dispo: new Set<Page>(["dashboard", "wholesale", "messages", "dialer", "contacts", "inbox", "tasks", "calendar"]),
  // Read-only financials + analytics.
  bookkeeper: new Set<Page>(["dashboard", "roi", "analytics"]),
  // Assigned tasks + assigned leads only.
  va: new Set<Page>(["dashboard", "properties", "tasks", "calendar"]),
};

/** Can this role see/open this page? */
export function canSeePage(role: string | null | undefined, page: Page): boolean {
  const r = (role ?? "member").trim().toLowerCase();
  if (ADMIN_ONLY.has(page)) return r === "admin";
  if (FULL.has(r)) return true;
  const set = RESTRICTED[r];
  if (!set) return true; // unknown role → legacy full (fail open, never lock out)
  return set.has(page);
}

/** The first page (in nav order) this role is allowed to land on. Used to
 * bounce a restricted role off a page they can't see (v1 bootApp's
 * `[...].find(can !== 'none') || 'settings'`). Always resolves to something —
 * every role can see the dashboard. */
export function firstAllowedPage(role: string | null | undefined): Page {
  return NAV_PAGES.find((p) => canSeePage(role, p)) ?? "dashboard";
}

/** True when the role is one of the named restricted roles (handy for showing
 * a "your view is limited to your role" hint, and for tests). */
export function isRestrictedRole(role: string | null | undefined): boolean {
  const r = (role ?? "").trim().toLowerCase();
  return r in RESTRICTED;
}

// ---- Per-user page permissions (PERMS settings blob) -----------------------
// Shape: { [userId]: { hidden: string[] } } — pages an admin has chosen to hide
// from that specific user, ON TOP OF the role gate above. Deliberately a DENY
// list so the safe default is FULL access:
//   * Admins are NEVER restricted (they can't lock themselves or a peer out of
//     admin surfaces by accident — an admin always sees everything).
//   * 'dashboard' is never hideable, so every user always has a landing page.
//   * Anything malformed (not an object, missing entry, non-array hidden, an
//     unknown page name) contributes NOTHING — it fails open.
// The whole feature is INERT until an admin writes an entry: with no PERMS blob
// (or no entry for a user) the hidden set is empty and behavior is byte-identical
// to before. That's what makes it safe to ship before it's been exercised live.

/** Pages that are never hideable, so a user can't be locked out entirely. */
export const NEVER_HIDDEN = new Set<Page>(["dashboard"]);

/** The set of pages hidden for one user by the PERMS blob (empty = nothing). */
/** Per-section access LEVEL (v1 None / View / Edit). Stored per user at
 * PERMS[userId].access[page] = "none" | "view"; absent = "edit" (full). Admins
 * always "edit". This is v1's tri-state, richer than the old binary hide list —
 * which is still honored (a page in the legacy `hidden[]` array reads as
 * "none") so nothing an admin set before is lost. */
export type PageAccess = "none" | "view" | "edit";

export function pageAccessFor(
  perms: unknown,
  userId: string | null | undefined,
  role: string | null | undefined,
  page: Page,
): PageAccess {
  if ((role ?? "").trim().toLowerCase() === "admin") return "edit";
  if (NEVER_HIDDEN.has(page)) return "edit"; // dashboard is always visible+full
  if (!userId || perms === null || typeof perms !== "object" || Array.isArray(perms)) return "edit";
  const entry = (perms as Record<string, unknown>)[userId];
  if (entry === null || typeof entry !== "object" || Array.isArray(entry)) return "edit";
  const e = entry as Record<string, unknown>;
  // legacy binary hide list → "none"
  if (Array.isArray(e["hidden"]) && (e["hidden"] as unknown[]).includes(page)) return "none";
  const acc = e["access"];
  if (acc !== null && typeof acc === "object" && !Array.isArray(acc)) {
    const v = (acc as Record<string, unknown>)[page];
    if (v === "none" || v === "view") return v;
  }
  return "edit";
}

export function hiddenPagesFor(
  perms: unknown,
  userId: string | null | undefined,
  role: string | null | undefined,
): Set<Page> {
  const empty = new Set<Page>();
  if ((role ?? "").trim().toLowerCase() === "admin") return empty; // admins: always full
  if (!userId) return empty;
  const out = new Set<Page>();
  for (const p of NAV_PAGES) {
    if (NEVER_HIDDEN.has(p)) continue;
    if (pageAccessFor(perms, userId, role, p) === "none") out.add(p);
  }
  return out;
}

/** Pages this user may SEE but not EDIT (access === "view"). The app downgrades
 * each such page's edit capability to read-only. */
export function readOnlyPagesFor(
  perms: unknown,
  userId: string | null | undefined,
  role: string | null | undefined,
): Set<Page> {
  const out = new Set<Page>();
  if ((role ?? "").trim().toLowerCase() === "admin" || !userId) return out;
  for (const p of NAV_PAGES) {
    if (pageAccessFor(perms, userId, role, p) === "view") out.add(p);
  }
  return out;
}

/** Can this user see the page, combining the role gate AND their per-user
 * hidden set? This is what the app should call; canSeePage stays the pure role
 * gate. Admin/full unchanged when the hidden set is empty. */
export function canSeePageFor(
  role: string | null | undefined,
  page: Page,
  hidden: Set<Page>,
): boolean {
  return canSeePage(role, page) && !hidden.has(page);
}

/** First page (nav order) this user may land on, honoring both the role gate
 * and the hidden set. Always resolves (dashboard is never hidden). */
export function firstVisiblePage(role: string | null | undefined, hidden: Set<Page>): Page {
  return NAV_PAGES.find((p) => canSeePageFor(role, p, hidden)) ?? "dashboard";
}

// ---- Per-user field-group hiding (v1 auth-users.js LEAD_FIELD_GROUPS) --------
// Same PERMS blob, same DENY-list discipline as page hiding: an admin writes
// entry.hiddenFields = [groupKey…] to hide sensitive lead-detail groups from one
// member. Admins are never restricted; unknown keys fail open; empty = nothing.

/** The sensitive lead-detail field groups an admin can hide (v1 verbatim). */
export const LEAD_FIELD_GROUPS: { key: string; label: string; desc: string }[] = [
  { key: "financials", label: "Financials & ROI", desc: "Offer / ARV / ROI numbers + the Est-ROI tile" },
  { key: "sellerContact", label: "Seller / owner contact", desc: "Seller contact card + county owner" },
  { key: "taxLegal", label: "Tax & legal", desc: "Tax history, parcel, assessed value" },
];
const LEAD_FIELD_GROUP_KEYS = new Set(LEAD_FIELD_GROUPS.map((g) => g.key));

/** v1 PERM_ACTIONS (auth-users.js) — capability toggles stored per-user in
 * PERMS[userId].actions[key] = true (absent/false = NOT allowed for a
 * restricted role; admins always allowed). Labels/descriptions verbatim. */
export const PERM_ACTIONS: { key: string; label: string; desc: string }[] = [
  { key: "delete", label: "Delete records", desc: "Remove properties, tasks" },
  { key: "export", label: "Export & share", desc: "Share property PDFs / export data" },
  { key: "convert", label: "Convert lead → deal", desc: "Turn a lead into a Transaction or Wholesale" },
  { key: "financials", label: "Edit deal financials", desc: "Change offer / contract / ARV numbers" },
  { key: "manageScrapers", label: "Manage scrapers", desc: "Create / enable scraper jobs" },
  { key: "manageTemplates", label: "Manage templates", desc: "Create / edit templates" },
];
const PERM_ACTION_KEYS = new Set(PERM_ACTIONS.map((a) => a.key));

/** Lead-scope options (v1 leadScope): "all" (default) or "assigned". */
export const LEAD_SCOPES: { key: string; label: string }[] = [
  { key: "all", label: "All leads" },
  { key: "assigned", label: "Only assigned to them" },
];

function permEntry(perms: unknown, userId: string | null | undefined): Record<string, unknown> | null {
  if (!userId || perms === null || typeof perms !== "object" || Array.isArray(perms)) return null;
  const e = (perms as Record<string, unknown>)[userId];
  return e !== null && typeof e === "object" && !Array.isArray(e) ? (e as Record<string, unknown>) : null;
}

/** Can this user perform a capability action? Admins always yes; the other FULL
 * roles (manager/member) also always yes — the SAFETY RULE at the top of this
 * file makes them full-access by default, exactly as v1 behaved when no per-user
 * perms were set. Only the named RESTRICTED roles are gated by the allow-list:
 * PERMS[id].actions[key] === true (v1 canDo semantics). This bypass is what
 * makes the whole feature safe to wire today — the current all-manager/member
 * team is never gated; the allow-list only bites the restricted roles (none
 * provisioned yet), which is precisely the matrix behavior we want when they are. */
export function canDoAction(perms: unknown, userId: string | null | undefined, role: string | null | undefined, action: string): boolean {
  const r = (role ?? "member").trim().toLowerCase();
  if (r === "admin" || FULL.has(r)) return true; // admin + manager/member: full by default
  if (!(r in RESTRICTED)) return true; // unknown role → fail open (a typo can't lock someone out)
  if (!PERM_ACTION_KEYS.has(action)) return true; // unknown action = not gated
  const e = permEntry(perms, userId);
  const acts = e?.["actions"];
  return !!(acts && typeof acts === "object" && !Array.isArray(acts) && (acts as Record<string, unknown>)[action] === true);
}

/** A user's lead scope ("all" | "assigned"). The FULL roles (admin/manager/member)
 * and any unknown role are always "all" — only a named restricted role can be
 * narrowed to "assigned", and only when PERMS says so. Fails open everywhere else. */
export function leadScopeFor(perms: unknown, userId: string | null | undefined, role: string | null | undefined): string {
  const r = (role ?? "member").trim().toLowerCase();
  if (r === "admin" || FULL.has(r) || !(r in RESTRICTED)) return "all";
  const e = permEntry(perms, userId);
  return e?.["leadScope"] === "assigned" ? "assigned" : "all";
}

/** v1 leadVisible: is a lead (assigned to `ownerId`, "" = unassigned) visible to a
 * user with this `scope`/`userId`? Scope "all" (admins, the whole current team,
 * unknown roles) always sees everything; "assigned" sees only leads whose owner
 * is this user. Because leadScopeFor already collapses non-restricted roles to
 * "all", this predicate is inert until a restricted "assigned" login exists. */
export function leadVisibleTo(scope: string, userId: string | null | undefined, ownerId: string | null | undefined): boolean {
  if (scope !== "assigned") return true;
  return !!userId && ownerId === userId;
}

/** The set of lead-detail field groups hidden for one user by the PERMS blob. */
export function hiddenFieldsFor(
  perms: unknown,
  userId: string | null | undefined,
  role: string | null | undefined,
): Set<string> {
  const empty = new Set<string>();
  if ((role ?? "").trim().toLowerCase() === "admin") return empty; // admins: always full
  if (!userId) return empty;
  if (perms === null || typeof perms !== "object" || Array.isArray(perms)) return empty;
  const entry = (perms as Record<string, unknown>)[userId];
  if (entry === null || typeof entry !== "object" || Array.isArray(entry)) return empty;
  const hid = (entry as Record<string, unknown>)["hiddenFields"];
  if (!Array.isArray(hid)) return empty;
  const out = new Set<string>();
  for (const k of hid) if (typeof k === "string" && LEAD_FIELD_GROUP_KEYS.has(k)) out.add(k);
  return out;
}
